import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { useAppSelector } from 'app/hooks';
import { AuthStatus } from 'features/auth/authSlice';
import type { IUser } from 'features/user/userAPI';
import { Role } from 'features/user/userAPI';
import { UserStateProps } from 'features/user/userSlice';
import { RootState } from '../app/store';

// Extended type that includes auth properties from authSlice, plus user and userId derived from authSlice
export type UserWithAuthProps = UserStateProps & {
  user: IUser;
  userId: number | undefined;
  authStatus: AuthStatus;
  isAuthReady: boolean;
  isAuthLoading: boolean;
  isAuthenticated: boolean;
};

export const useUser = (): UserWithAuthProps => {
  // Get user from authSlice (single source of truth for auth)
  const authState = useAppSelector(
    (state: RootState) => state.auth,
    shallowEqual,
  );
  const intl = useAppSelector((state: RootState) => state.intl, shallowEqual);

  // Use user from authSlice
  const user = authState.user;

  return useMemo(
    () => ({
      // userLoggedIn: Boolean(Cookies.get('token')),
      user: user,
      userId: user?.id,
      userIsAdmin: user && (user.roles?.includes(Role.admin) ?? false),
      userIsManager:
        user &&
        ((user.roles?.includes(Role.manager) ||
          user.roles?.includes(Role.admin)) ??
          false),
      locale: intl.locale,
      // Use authSlice status properties
      authStatus: authState.status,
      isAuthReady: authState.status !== 'checking',
      isAuthLoading:
        authState.status === 'loading' || authState.status === 'checking',
      isAuthenticated: authState.status === 'authenticated',
    }),
    [user, authState.status, intl.locale],
  );
};
